import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import { Spin } from "antd";
import {
  getApiBaseUrl,
  ADD_LOCATION,
  ADD_ACTIVITY,
  LOGIN,
} from "../../Config/Config";

const AddFunctionalLocations = () => {
  const [formData, setFormData] = useState({
    BlockName: "",
    Description: "",
  });
  const [loading, setLoading] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const [askPassword, setAskPassword] = useState(false);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const user = JSON.parse(sessionStorage.getItem("user") || "{}");

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError(""); // Clear error on input change
  };

  const handleAuth = async () => {
    if (!password) {
      setError("Please enter your password.");
      return;
    }

    setAuthLoading(true);
    setError("");

    try {
      if (!user?.UserName) {
        throw new Error("User name not found in session data.");
      }

      const payload = {
        username: user.UserName,
        password,
      };

      const res = await fetch(`${getApiBaseUrl()}${LOGIN}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (data.status) {
        setAskPassword(false);
        setPassword("");
        await performAdd();
      } else {
        throw new Error(
          data.Message || "Invalid credentials. Please try again."
        );
      }
    } catch (err) {
      setError(err.message || "Unable to authenticate. Please try again.");
    } finally {
      setAuthLoading(false);
    }
  };

  const performAdd = async () => {
    const logActivity = async (isSuccess, notes) => {
      try {
        await fetch(`${getApiBaseUrl()}${ADD_ACTIVITY}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ActivityType: 1,
            PerformedBy: user?.UserID,
            PerformedOn: new Date()
              .toISOString()
              .slice(0, 19)
              .replace("T", " "),
            Notes: notes,
            Location: "addLocation",
            IsSucces: isSuccess ? 1 : 0,
          }),
        });
      } catch (err) {
        console.error("Failed to log activity:", err);
      }
    };

    const payload = {
      BlockName: formData.BlockName || "",
      Description: formData.Description || "",
      CreateBy: user?.UserID,
      IsLocationType: 0,
    };

    try {
      setLoading(true);

      const response = await fetch(`${getApiBaseUrl()}${ADD_LOCATION}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const result = await response.json();
      const status = result?.ResultData?.[0];

      if (response.ok && status?.Status === "Success") {
        await logActivity(
          true,
          `Block "${formData.BlockName}" created successfully`
        );
        setFormData({ BlockName: "", Description: "" });
        Swal.fire("Success", "Block added successfully!", "success").then(
          () => {
            window.location.reload();
          }
        );
      } else {
        await logActivity(
          false,
          `Block "${formData.BlockName}" creation failed`
        );
        Swal.fire("Failed", "Something went wrong!", "error");
      }
    } catch (error) {
      console.error("Network error:", error);
      await logActivity(
        false,
        `Block "${formData.BlockName}" creation failed: ${error.message}`
      );
      Swal.fire("Error", "Unable to connect to server.", "error");
    } finally {
      setLoading(false);
      setAskPassword(false);
      setPassword("");
      setError("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.BlockName || !formData.Description) {
      setError(
        "Please fill in all required fields: Block Name and Description."
      );
      Swal.fire({
        icon: "warning",
        title: "Validation Error",
        text: "Please fill in all required fields: Block Name and Description.",
      });
      return;
    }

    if (!user?.UserID) {
      setError("Invalid user data. Please log in again.");
      Swal.fire({
        icon: "error",
        title: "User Data Error",
        text: "Invalid user data. Please log in again.",
      });
      return;
    }

    setAskPassword(true);
  };

  const clearUserData = () => {
    setFormData({ BlockName: "", Description: "" });
    setAskPassword(false);
    setPassword("");
    setError("");
  };

  useEffect(() => {
    const offcanvas = document.getElementById("AddBlocksOffcanvas");
    if (offcanvas) {
      const handleClose = () => {
        clearUserData();
      };
      offcanvas.addEventListener("hidden.bs.offcanvas", handleClose);
      return () => {
        offcanvas.removeEventListener("hidden.bs.offcanvas", handleClose);
      };
    }
  }, []);

  return (
    <>
      <style>
        {`
        @media (max-width: 576px) {
          #AddBlocksOffcanvas {
            width: 90% !important;
          }
        }

        @media (min-width: 577px) and (max-width: 992px) {
          #AddBlocksOffcanvas {
            width: 55% !important;
          }
        }

        @media (min-width: 993px) and (max-width: 1440px) {
          #AddBlocksOffcanvas {
            width: 24% !important;
          }
        }
          
        @media (min-width: 1441px)  {
          #AddBlocksOffcanvas {
            width: 15% !important;
          }
        }
        `}
      </style>
      {(loading || authLoading) && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{
            zIndex: 1050,
            backdropFilter: "blur(4px)",
            backgroundColor: "rgba(255, 255, 255, 0.2)",
          }}
        >
          <Spin size="large" tip="Processing..." />
        </div>
      )}
      <div
        className="offcanvas offcanvas-end"
        tabIndex="-1"
        id="AddBlocksOffcanvas"
      >
        <form onSubmit={handleSubmit}>
          <div className="offcanvas-header">
            <h5 className="offcanvas-title">Add Block</h5>
            <div className="d-flex align-items-center ms-auto">
              <button
                type="submit"
                className="btn btn-sm btn-outline-success me-2"
                title="Save"
                disabled={askPassword}
              >
                <i className="fa fa-check fs-4"></i>
              </button>
              <button
                type="button"
                className="btn-close text-reset"
                data-bs-dismiss="offcanvas"
                aria-label="Close"
                disabled={askPassword}
              ></button>
            </div>
          </div>
          <div className="offcanvas-body">
            {error && (
              <div className="alert alert-danger" role="alert">
                {error}
              </div>
            )}
            <div className="mb-3">
              <label className="form-label">Block Name</label>{" "}
              <span className="text-danger ms-1">*</span>
              <input
                type="text"
                name="BlockName"
                value={formData.BlockName}
                onChange={handleInputChange}
                className="form-control"
                placeholder="Enter block name"
                required
                disabled={askPassword}
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Description</label>{" "}
              <span className="text-danger ms-1">*</span>
              <textarea
                name="Description"
                value={formData.Description}
                onChange={handleInputChange}
                className="form-control"
                placeholder="Enter description"
                required
                disabled={askPassword}
              ></textarea>
            </div>
            {askPassword && (
              <div className="mt-2">
                <div className="mb-3 w-lg-75 m-auto">
                  <label className="form-label">
                    Confirm Password <span className="text-danger"> *</span>
                  </label>
                  <div className="pass-group">
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      className="pass-input form-control"
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        setError("");
                      }}
                      onCopy={(e) => e.preventDefault()}
                      onPaste={(e) => e.preventDefault()}
                      onCut={(e) => e.preventDefault()}
                      placeholder="Enter Password"
                    />
                    <span
                      className={`ti toggle-password ${
                        showPassword ? "ti-eye" : "ti-eye-off"
                      }`}
                      onClick={() => setShowPassword(!showPassword)}
                    ></span>
                  </div>
                </div>
                <button
                  className="btn btn-primary btn-sm me-2"
                  onClick={handleAuth}
                  disabled={authLoading || !password}
                >
                  {authLoading ? "Verifying..." : "Confirm"}
                </button>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => {
                    setAskPassword(false);
                    setPassword("");
                    setError("");
                  }}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </form>
      </div>
    </>
  );
};

export default AddFunctionalLocations;